import {
  getAllRegions,
  getCommuneByDistrict,
  getDistrictByRegion,
  getFokontanysByCommune,
} from "@core/actions/localisationAction.ts";
import { FetchStatusType } from "@core/modeles/model.ts";
import { RootState } from "@core/store/store.ts";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  CommuneDTO,
  DistrictDTO,
  FokontanyDTO,
  RegionDTO,
} from "@utils/appUtils.ts";

interface LocalisationState {
  regionList: RegionDTO[];
  districtList: DistrictDTO[];
  communeList: CommuneDTO[];
  fokontanyList: FokontanyDTO[];
  loadingLocalisation: FetchStatusType;
  loadingDistrict: FetchStatusType;
  loadingCommune: FetchStatusType;
  loadingFokotany: FetchStatusType;
}

const initialState: LocalisationState = {
  regionList: [],
  districtList: [],
  communeList: [],
  fokontanyList: [],
  loadingLocalisation: FetchStatusType.IDLE,
  loadingDistrict: FetchStatusType.IDLE,
  loadingCommune: FetchStatusType.IDLE,
  loadingFokotany: FetchStatusType.IDLE,
};

const LocalisationSlice = createSlice({
  name: "localisation",
  initialState,
  reducers: {
    setRegionList(state, action: PayloadAction<RegionDTO[]>) {
      return { ...state, regionList: action.payload };
    },
    setDistrictList(state, action: PayloadAction<DistrictDTO[]>) {
      return { ...state, regionFiltered: action.payload };
    },

    setCommuneList(state, action: PayloadAction<CommuneDTO[]>) {
      return { ...state, communeList: action.payload };
    },

    setFokontanyList(state, action: PayloadAction<FokontanyDTO[]>) {
      return { ...state, fokontanyList: action.payload };
    },
  },
  extraReducers: (builder) => {
    // lists region
    builder
      .addCase(getAllRegions.pending, (state) => {
        return { ...state, loadingLocalisation: FetchStatusType.LOADING };
      })
      .addCase(getAllRegions.fulfilled, (state, action) => {
        return {
          ...state,
          loadingLocalisation: FetchStatusType.COMPLETED,
          regionList: action.payload.data,
        };
      })
      .addCase(getAllRegions.rejected, (state) => {
        return { ...state, loadingLocalisation: FetchStatusType.FAILED };
      });

    //list district by region
    builder
      .addCase(getDistrictByRegion.pending, (state) => {
        return { ...state, loadingDistrict: FetchStatusType.LOADING };
      })
      .addCase(getDistrictByRegion.fulfilled, (state, action) => {
        return {
          ...state,
          loadingDistrict: FetchStatusType.COMPLETED,
          districtList: action.payload.data,
        };
      })
      .addCase(getDistrictByRegion.rejected, (state) => {
        return { ...state, loadingDistrict: FetchStatusType.FAILED };
      });

    //list commune by district
    builder
      .addCase(getCommuneByDistrict.pending, (state) => {
        return { ...state, loadingCommune: FetchStatusType.LOADING };
      })
      .addCase(getCommuneByDistrict.fulfilled, (state, action) => {
        return {
          ...state,
          loadingCommune: FetchStatusType.COMPLETED,
          communeList: action.payload.data,
        };
      })
      .addCase(getCommuneByDistrict.rejected, (state) => {
        return { ...state, loadingCommune: FetchStatusType.FAILED };
      });

    //list fokontany by commune
    builder
      .addCase(getFokontanysByCommune.pending, (state) => {
        return { ...state, loadingFokotany: FetchStatusType.LOADING };
      })
      .addCase(getFokontanysByCommune.fulfilled, (state, action) => {
        return {
          ...state,
          loadingFokotany: FetchStatusType.COMPLETED,
          fokontanyList: action.payload.data,
        };
      })
      .addCase(getFokontanysByCommune.rejected, (state) => {
        return { ...state, loadingFokotany: FetchStatusType.FAILED };
      });
  },
});

export const {
  setRegionList,
  setDistrictList,
  setCommuneList,
  setFokontanyList,
} = LocalisationSlice.actions;

export const regionList = (state: RootState) => state.localisation.regionList;
export const districtList = (state: RootState) =>
  state.localisation.districtList;
export const communeList = (state: RootState) => state.localisation.communeList;
export const fokontanyList = (state: RootState) =>
  state.localisation.fokontanyList;

export const loadingDistrict = (state: RootState) =>
  state.localisation.loadingDistrict;

export const loadingLocalisation = (state: RootState) =>
  state.localisation.loadingLocalisation;

export const loadingCommune = (state: RootState) =>
  state.localisation.loadingCommune;

export const loadingFokotany = (state: RootState) =>
  state.localisation.loadingFokotany;


export default LocalisationSlice.reducer;
